import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../../context/settings";
import Drawer from "react-modern-drawer";
import { Spinner } from "reactstrap";
import { IoClose, IoCreateOutline, IoPrintOutline, IoTrashOutline } from "react-icons/io5";
import { get } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import DefaultButton from "../../../../../components/Buttons/Default";
import DOC_STYLES from "../../../../Sales/utils/docStyles";
import { AWS_S3_URL } from "../../../../../utils/constants";
import { printSlug } from "../../../../../utils/helpers/text";

const PRIMARY = "#6C63FF";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ViewDrawer({ paymentId, isOpen, onClose, onDelete, refreshPayments }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const [payment, setPayment] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getPayment = async () => {
    if (!paymentId) return;
    try {
      const data = await get(`/purchases/payments/view/${paymentId}`);
      setPayment(data.data ?? null);
      setCompanyData(data.company ?? null);
    } catch {
      toast.error(t('sales.failedLoadPayment'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!paymentId) return;
    setLoading(true);
    setPayment(null);
    getPayment();
  }, [paymentId]);

  const supplierName = payment?.supplier
    ? (payment.supplier.company_name || `${payment.supplier.firstname ?? ""} ${payment.supplier.lastname ?? ""}`.trim())
    : "—";

  const currency = (payment?.currency ?? "usd").toUpperCase();

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      direction="right"
      size={680}
      enableOverlay
      lockScroll
      zIndex={9000}
      duration={200}
    >
      <div style={DOC_STYLES.wrap}>

        {/* ── Header ── */}
        <div style={DOC_STYLES.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
              onClick={onClose}
              onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
            >
              <IoClose size={20} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{t('purchases.paymentDetails')}</span>
          </div>
        </div>

        {/* ── Action Bar ── */}
        <div style={DOC_STYLES.actionBar}>
          <DefaultButton
            className="btn-action"
            size="sm"
            onClick={() => { onClose(); navigate(`/purchases/payments/edit/${paymentId}`, { state: { paymentData: payment } }); }}
            disabled={!payment}
            color="light"
            outline
          >
            <IoCreateOutline size={15} />
            {t('common.edit')}
          </DefaultButton>
          <DefaultButton
            className="btn-action"
            size="sm"
            onClick={() => window.print()}
            disabled={!payment}
            color="light"
            outline
          >
            <IoPrintOutline size={15} />
            {t('common.print')}
          </DefaultButton>
          <DefaultButton
            className="btn-action"
            size="sm"
            onClick={onDelete}
            disabled={!payment}
            color="light"
            outline
          >
            <IoTrashOutline size={15} />
            {t('common.delete')}
          </DefaultButton>
        </div>

        {/* ── Scrollable document area ── */}
        <div style={DOC_STYLES.scroll}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
              <Spinner color="secondary" />
            </div>
          )}

          {!loading && payment && (
            <div id="quote-print-area" style={DOC_STYLES.paper}>

              {/* Company + Title */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
                <div>
                  <img 
                    src={companyData?.logo_path ? AWS_S3_URL + companyData.logo_path : require('../../../../../assets/images/brand.svg').default} 
                    height={38} 
                    style={{ marginBottom: 10 }}
                    alt={t('settings.logo')}
                  />
                  <div style={{ fontWeight: 800, fontSize: 18, color: "#1d1d1f", lineHeight: 1.2 }}>
                    { companyData?.legal_name || t('common.yourCompanyName') }
                  </div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                    { companyData?.street_address || t('common.companyStreetAddress') }<br />
                    { companyData?.city || t('settings.city') } { companyData?.state || t('common.state') } { companyData?.postal_code || t('settings.postalCode') }<br />
                    { printSlug(companyData?.country) || t('common.country') }<br />
                    { companyData?.email || t('common.emailExample') }
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 800, fontSize: 26, color: PRIMARY, letterSpacing: 2, textTransform: "uppercase", lineHeight: 1 }}>
                    {t('purchases.paymentReceipt')}
                  </div>
                </div>
              </div>

              <hr style={DOC_STYLES.divider} />

              {/* Amount Hero */}
              <div style={{ background: "linear-gradient(135deg, #f4f2ff 0%, #ede9ff 100%)", border: "1px solid #ddd6ff", borderRadius: 12, padding: "28px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: "#9b93e0", textTransform: "uppercase", marginBottom: 6 }}>{t('common.amountPaid')}</div>
                  <div style={{ fontSize: 42, fontWeight: 800, color: PRIMARY, lineHeight: 1 }}>
                    {fmt(payment.amount)} {currency}
                  </div>
                </div>
                <div style={{ background: "#fff", border: "1px solid #ddd6ff", borderRadius: 10, padding: "16px 24px", textAlign: "right", minWidth: 200 }}>
                  <div style={{ marginBottom: 10 }}>
                    <div style={DOC_STYLES.label}>{t('sales.paymentDate')}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                      {formatDate(payment.payment_date) || "—"}
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={DOC_STYLES.label}>{t('purchases.referenceHash')}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                      {payment.reference || t('common.notAvailable')}
                    </div>
                  </div>
                  <div>
                    <div style={DOC_STYLES.label}>{t('sales.paymentMethod')}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                      {payment.payment_method || t('common.notAvailable')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Paid To */}
              <div style={{ marginBottom: 32 }}>
                <div style={DOC_STYLES.label}>{t('sales.paidTo')}</div>
                <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: "16px 20px", marginTop: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f", marginBottom: 4 }}>
                    {supplierName}
                  </div>
                  {payment.supplier?.address_line_1 && (
                    <div style={{ fontSize: 12, color: "#777", lineHeight: 1.8 }}>
                      {payment.supplier.address_line_1}<br />
                      {[payment.supplier.city, payment.supplier.state, payment.supplier.postal_code].filter(Boolean).join(", ")}<br />
                      {payment.supplier.country}
                    </div>
                  )}
                  {payment.supplier?.email && (
                    <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>{payment.supplier.email}</div>
                  )}
                </div>
              </div>

              {/* Payment For */}
              {payment.bill && (
                <>
                  <hr style={DOC_STYLES.divider} />
                  <div style={DOC_STYLES.label}>{t('sales.paymentFor')}</div>
                  <table style={{ ...DOC_STYLES.itemsTable, marginTop: 10 }}>
                    <thead>
                      <tr>
                        <th style={{ ...DOC_STYLES.th, borderRadius: "6px 0 0 0" }}>{t('purchases.billNumber')}</th>
                        <th style={DOC_STYLES.th}>{t('sales.dueDate')}</th>
                        <th style={DOC_STYLES.th}>{t('common.status')}</th>
                        <th style={{ ...DOC_STYLES.thRight, borderRadius: "0 6px 0 0" }}>{t('common.amountPaid')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: "#fff" }}>
                        <td style={DOC_STYLES.td}>
                          <span style={{ fontWeight: 600, color: PRIMARY }}>{payment.bill.bill_number}</span>
                        </td>
                        <td style={DOC_STYLES.td}>{formatDate(payment.bill.due_date) || "—"}</td>
                        <td style={DOC_STYLES.td}>{payment.bill.status ?? "—"}</td>
                        <td style={DOC_STYLES.tdRight}>{fmt(payment.amount)} {currency}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}

              {/* Notes */}
              {payment.notes && (
                <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #eee" }}>
                  <div style={DOC_STYLES.label}>{t('common.notes')}</div>
                  <p style={{ fontSize: 13, color: "#555", whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.7 }}>
                    {payment.notes}
                  </p>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: 40, paddingTop: 20, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.8 }}>
                  {t('purchases.computerGeneratedReceipt')}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
